// w18 Olov Kechasi — baroque-gold wedding preview card.
//
// Dark, regal mood: deep midnight paper inside an antique-gold filigree
// frame. The card follows the operator-approved "outer frame + inner
// card" structure but channels it through baroque idioms — the frame is
// a layered gold foil gradient with corner cartouches, the focal element
// is an oval photo locket inside a gilt ring crowned by a small crown +
// laurel cluster, and the names sit beneath in Cinzel.
import { Photo, type LayoutCtx } from "./_shared";

// Antique-gold foil — alternating warm and bright gold stops.
const GOLD_FOIL =
  "linear-gradient(135deg, #8B6F2B 0%, #D4B262 22%, #F1D77A 45%, #D4B262 65%, #8B6F2B 100%)";

export function BaroqueGoldLayout({
  names,
  date,
  venue,
  accent,
  ink,
  bg,
  font,
  photo,
}: LayoutCtx) {
  const captionDate = formatDate(date);
  const gold = accent;

  return (
    <div
      style={{
        position: "absolute",
        inset: 0,
        background: GOLD_FOIL,
        padding: "5%",
        display: "flex",
      }}
    >
      <div
        style={{
          flex: 1,
          background: bg,
          color: ink,
          position: "relative",
          padding: "9%",
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          textAlign: "center",
          boxShadow: `inset 0 0 0 1px ${gold}aa, inset 0 0 0 4px ${bg}, inset 0 0 0 4.5px ${gold}66`,
        }}
      >
        {/* Corner cartouches */}
        <svg
          aria-hidden
          viewBox="0 0 100 140"
          preserveAspectRatio="none"
          style={{ position: "absolute", inset: 0, width: "100%", height: "100%", pointerEvents: "none" }}
        >
          <g stroke={gold} strokeWidth="0.4" fill="none">
            {/* TL */}
            <path d="M6 18 C 14 8 18 6 28 6 M6 18 C 8 14 12 12 16 12" />
            <circle cx="10" cy="10" r="0.9" fill={gold} />
            {/* TR */}
            <path d="M94 18 C 86 8 82 6 72 6 M94 18 C 92 14 88 12 84 12" />
            <circle cx="90" cy="10" r="0.9" fill={gold} />
            {/* BL */}
            <path d="M6 122 C 14 132 18 134 28 134 M6 122 C 8 126 12 128 16 128" />
            <circle cx="10" cy="130" r="0.9" fill={gold} />
            {/* BR */}
            <path d="M94 122 C 86 132 82 134 72 134 M94 122 C 92 126 88 128 84 128" />
            <circle cx="90" cy="130" r="0.9" fill={gold} />
          </g>
        </svg>

        {/* Eyebrow */}
        <div
          style={{
            fontFamily: "var(--font-ui)",
            fontSize: 7,
            fontWeight: 700,
            letterSpacing: "0.45em",
            color: gold,
            marginBottom: "5%",
          }}
        >
          NIKOH MAROSIMI
        </div>

        {/* Oval photo locket with gilt ring + tiny crown */}
        <div
          style={{
            width: "58%",
            aspectRatio: "1 / 1.15",
            position: "relative",
            marginBottom: "6%",
          }}
        >
          {/* Crown */}
          <svg
            viewBox="0 0 60 18"
            style={{
              position: "absolute",
              top: -10,
              left: "50%",
              transform: "translateX(-50%)",
              width: "40%",
              color: gold,
            }}
            aria-hidden
          >
            <path d="M2 16 L 10 4 L 18 14 L 26 0 L 30 14 L 34 0 L 42 14 L 50 4 L 58 16 Z"
              fill={gold} stroke={ink} strokeWidth="0.3" />
            <circle cx="10" cy="6" r="1.2" fill={ink} />
            <circle cx="30" cy="2" r="1.4" fill={ink} />
            <circle cx="50" cy="6" r="1.2" fill={ink} />
          </svg>

          {/* Gilt outer ring */}
          <div
            style={{
              position: "absolute",
              inset: 0,
              borderRadius: "50%",
              background: GOLD_FOIL,
              padding: "6%",
            }}
          >
            <div
              style={{
                width: "100%",
                height: "100%",
                borderRadius: "50%",
                background: bg,
                padding: "3%",
                boxSizing: "border-box",
              }}
            >
              <div
                style={{
                  width: "100%",
                  height: "100%",
                  borderRadius: "50%",
                  overflow: "hidden",
                  position: "relative",
                }}
              >
                <Photo
                  src={photo}
                  style={{ position: "absolute", inset: 0, width: "100%", height: "100%" }}
                  fallback={
                    <div
                      style={{
                        position: "absolute",
                        inset: 0,
                        background: `radial-gradient(circle at 50% 45%, ${gold}55, ${bg})`,
                        display: "grid",
                        placeItems: "center",
                        fontFamily: font,
                        color: gold,
                        fontSize: 20,
                      }}
                    >
                      S & O
                    </div>
                  }
                  sepia
                />
              </div>
            </div>
          </div>
        </div>

        {/* Names */}
        <div
          style={{
            fontFamily: font,
            fontSize: 22,
            lineHeight: 1.05,
            color: ink,
            letterSpacing: "0.05em",
          }}
        >
          {names}
        </div>

        {/* Decorative divider */}
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: 6,
            marginTop: 8,
          }}
        >
          <span style={{ width: 24, height: 1, background: gold, opacity: 0.7 }} />
          <svg width="8" height="8" viewBox="0 0 10 10" aria-hidden>
            <path d="M5 0 L 6 4 L 10 5 L 6 6 L 5 10 L 4 6 L 0 5 L 4 4 Z" fill={gold} />
          </svg>
          <span style={{ width: 24, height: 1, background: gold, opacity: 0.7 }} />
        </div>

        <div style={{ flex: 1 }} />

        {/* Caption */}
        <div
          style={{
            fontFamily: "var(--font-ui)",
            fontSize: 6.5,
            fontWeight: 700,
            letterSpacing: "0.32em",
            color: gold,
            marginTop: "4%",
          }}
        >
          NIKOH · {captionDate}
        </div>
        <div
          style={{
            fontFamily: "var(--font-ui)",
            fontSize: 6,
            fontWeight: 600,
            letterSpacing: "0.28em",
            color: `${ink}aa`,
            marginTop: 6,
          }}
        >
          {venue.toUpperCase()}
        </div>
      </div>
    </div>
  );
}

function formatDate(d: string): string {
  const m = d.match(/^(\d{1,2})\.(\d{1,2})\.(\d{4})$/);
  if (!m) return d.toUpperCase();
  const day = parseInt(m[1], 10);
  const month = parseInt(m[2], 10);
  const year = m[3];
  const months = [
    "YANVAR", "FEVRAL", "MART", "APREL", "MAY", "IYUN",
    "IYUL", "AVGUST", "SENTABR", "OKTABR", "NOYABR", "DEKABR",
  ];
  return `${day} ${months[month - 1] ?? ""} ${year}`;
}
